import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { STALE } from '@/lib/query-cache.js';
import type { JiraIssue, JiraIssueSummary, AuditEntry } from '@shared/types.js';
import type { UploadedFile } from '@/features/editor/RichEditor.js';

/** How often a page in view re-reads Jira, so changes made elsewhere show without a reload. Paused while the tab is hidden. */
export const LIVE_REFRESH_MS = STALE.ACTIVE;

/**
 * For every view of ticket state (an issue page, lists, boards, Following). A copy kept in this
 * tab, or restored from the browser's storage on a reload, shows at once, but opening, reloading,
 * returning to the view or switching back to it (another issue, another filter) always re-reads
 * the server. Tickets change elsewhere (Nova, Jira, another person), and a copy counted as fresh
 * hid Nova's reassignment of SR-11 through three reloads while the activity log on the same page
 * already showed it (LH-1494). Never fresh: a page that switches key keeps its observer, and only
 * staleness decides whether the new key is read.
 */
export const LIVE_VIEW = {
  staleTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
  refetchInterval: LIVE_REFRESH_MS,
} as const;

/**
 * Refresh every view that can show an issue after it changes: its detail page,
 * list/search tables, boards, its transitions and the portal activity log.
 */
export function invalidateIssueViews(queryClient: QueryClient, issueKey: string): void {
  // Every open issue, not only this one: a parent lists its children's status
  void queryClient.invalidateQueries({ queryKey: ['issue'] });
  void queryClient.invalidateQueries({ queryKey: ['issues'] });
  void queryClient.invalidateQueries({ queryKey: ['board'] });
  void queryClient.invalidateQueries({ queryKey: ['transitions', issueKey] });
  void queryClient.invalidateQueries({ queryKey: ['audit', issueKey] });
  void queryClient.invalidateQueries({ queryKey: ['follows'] });
}

export function useIssueSearch(jql: string, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: ['issues', 'search', jql, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams({ jql, page: String(page), pageSize: String(pageSize) });
      const res = await api.get<JiraIssueSummary[]>(`/api/issues/search?${params}`);
      const meta = res.meta as Record<string, unknown> | undefined;
      return { issues: res.data, total: (meta?.total as number) ?? 0, page, pageSize, isLast: (meta?.isLast as boolean) ?? true };
    },
    enabled: jql.length > 0,
    ...LIVE_VIEW,
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch ALL issues for a JQL query by paging through all results.
 * Used for DataTable-style views where client-side search/filter/pagination
 * needs the full dataset.
 */
export function useAllIssues(jql: string, pageSize = 100) {
  return useQuery({
    queryKey: ['issues', 'all', jql],
    queryFn: async () => {
      const all: JiraIssueSummary[] = [];
      let page = 1;
      let isLast = false;

      while (!isLast) {
        const params = new URLSearchParams({ jql, page: String(page), pageSize: String(pageSize) });
        const res = await api.get<JiraIssueSummary[]>(`/api/issues/search?${params}`);
        all.push(...res.data);
        const meta = res.meta as Record<string, unknown> | undefined;
        isLast = (meta?.isLast as boolean) ?? true;
        page++;

        // Safety: cap at 2000 issues to avoid runaway fetches
        if (all.length >= 2000) break;
      }

      return all;
    },
    enabled: jql.length > 0,
    // These lists are where people look to see what is open. Tickets change
    // elsewhere (other people, Jira, Nova), so refresh on return and while in view;
    // a 24-hour copy showed finished and deleted tickets as open.
    ...LIVE_VIEW,
    placeholderData: (prev) => prev,
  });
}

export function useIssue(key: string) {
  return useQuery({
    queryKey: ['issue', key],
    queryFn: () => api.get<JiraIssue>(`/api/issues/${key}`).then((r) => r.data),
    enabled: !!key,
    ...LIVE_VIEW,  // assignee, subtasks and blockers change from other pages, Jira and Nova
  });
}

export function useTransitionIssue(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transitionId: string) =>
      api.post(`/api/issues/${issueKey}/transitions`, { transitionId }),
    onSuccess: () => invalidateIssueViews(queryClient, issueKey),
  });
}

interface NewChildIssue {
  summary: string;
  assignee?: string;
  issuetype?: string;
}

/** Add a subtask to a task, or a work item to an epic. The server picks the project and type. */
export function useCreateChildIssue(parentKey: string, projectKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ summary, assignee, issuetype }: NewChildIssue) =>
      api.post<{ key: string; reporterWarning?: string }>('/api/issues', {
        project: projectKey,
        issuetype: issuetype || 'Task',
        summary,
        assignee: assignee || undefined,
        parent: parentKey,
      }).then((r) => r.data),
    onSuccess: () => invalidateIssueViews(queryClient, parentKey),
  });
}

export function useUploadAttachments(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) =>
      api.postForm(`/api/issues/${issueKey}/attachments`, formData),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['audit', issueKey] });
    },
  });
}

export function useDeleteAttachment(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId: string) =>
      api.delete(`/api/attachments/${attachmentId}?issueKey=${issueKey}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['audit', issueKey] });
    },
  });
}

export function useIssueTransitions(issueKey: string, enabled = false) {
  return useQuery({
    queryKey: ['transitions', issueKey],
    queryFn: () => api.get<Array<{ id: string; name: string; to: { name: string } }>>(`/api/issues/${issueKey}/transitions`).then((r) => r.data),
    enabled: enabled && !!issueKey,
    staleTime: STALE.ACTIVE,
  });
}

/** Upload one file for the rich editor to embed; refreshes the issue's attachment list. */
export function useEditorUpload(issueKey: string) {
  const queryClient = useQueryClient();

  return useCallback(async (file: File): Promise<UploadedFile> => {
    const form = new FormData();
    form.append('files', file);
    const res = await api.postForm<Array<{ id: string; filename: string; mimeType: string; mediaApiFileId: string | null }>>(
      `/api/issues/${issueKey}/attachments`, form,
    );
    const a = res.data[0];
    if (!a) throw new Error('Upload returned no attachment');
    void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
    void queryClient.invalidateQueries({ queryKey: ['audit', issueKey] });
    return { attachmentId: String(a.id), mediaApiFileId: a.mediaApiFileId, filename: a.filename, mimeType: a.mimeType };
  }, [issueKey, queryClient]);
}

export function useUpdateIssueField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueKey, fields }: { issueKey: string; fields: Record<string, unknown> }) =>
      api.put(`/api/issues/${issueKey}`, fields),
    onSuccess: (_data, { issueKey }) => invalidateIssueViews(queryClient, issueKey),
  });
}

export function useInlineTransition(_projectKey?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueKey, transitionId }: { issueKey: string; transitionId: string }) =>
      api.post(`/api/issues/${issueKey}/transitions`, { transitionId }),
    onSuccess: (_data, { issueKey }) => invalidateIssueViews(queryClient, issueKey),
  });
}

export function useAuditLog(issueKey: string) {
  return useQuery({
    queryKey: ['audit', issueKey],
    queryFn: () => api.get<AuditEntry[]>(`/api/issues/${issueKey}/audit`).then((r) => r.data),
    enabled: !!issueKey,
    ...LIVE_VIEW,
  });
}
